import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Mail, 
  Settings, 
  Users, 
  FileText, 
  Send, 
  BarChart3,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Star,
  Plus,
  Search,
  Upload
} from "lucide-react";

const HowToSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Card className="border-slate-200">
      <CardHeader 
        className="cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="font-heading flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Icon className="w-5 h-5 text-orange-600" />
            </div>
            <span>{title}</span>
          </div>
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </CardTitle>
      </CardHeader>
      {isOpen && (
        <CardContent className="pt-0">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

const Step = ({ number, title, children }) => (
  <div className="flex gap-4 mb-4">
    <div className="flex-shrink-0 w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="font-semibold text-slate-900 mb-1">{title}</h4>
      <div className="text-slate-600 text-sm">{children}</div>
    </div>
  </div>
);

const Tip = ({ type = "info", children }) => {
  const styles = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    success: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warning: "bg-amber-50 border-amber-200 text-amber-800",
  };
  
  const icons = {
    info: AlertCircle,
    success: CheckCircle,
    warning: AlertCircle,
  };
  
  const Icon = icons[type];
  
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border ${styles[type]} text-sm mt-4`}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div>{children}</div>
    </div>
  );
};

export const HelpPage = () => {
  return (
    <div className="space-y-6 animate-fade-in" data-testid="help-page">
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight text-slate-900">How To Guide</h1>
        <p className="text-slate-500 mt-1">Learn how to set up and use Email Campaign Pro</p>
      </div>

      {/* Quick Start */}
      <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-white">
        <CardHeader>
          <CardTitle className="font-heading text-orange-900">Quick Start Guide</CardTitle>
          <CardDescription className="text-orange-700">
            Get up and running in 5 minutes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-orange-100">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">1</div>
              <span className="text-sm font-medium text-slate-700">Connect Gmail</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-orange-100">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">2</div>
              <span className="text-sm font-medium text-slate-700">Add Contacts</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-orange-100">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">3</div>
              <span className="text-sm font-medium text-slate-700">Create Template</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-orange-100">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">4</div>
              <span className="text-sm font-medium text-slate-700">Send Campaign</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connect Email Account */}
      <HowToSection title="Connecting Your Email Account" icon={Mail} defaultOpen={true}>
        <div className="space-y-6">
          <p className="text-slate-600">
            To send emails through the platform, you need to connect at least one Gmail account. 
            You can connect multiple accounts and choose which one to use for each campaign.
          </p>

          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 text-lg">Connecting a Gmail Account</h3>
            
            <Step number="1" title="Go to Settings">
              Click on <strong>Settings</strong> in the left sidebar navigation.
            </Step>
            
            <Step number="2" title="Click Add Account">
              In the Email Accounts section, click the <strong>"Add Account"</strong> button in the top right.
            </Step>
            
            <Step number="3" title="Sign in with Google">
              A Google sign-in window will appear. Select the Gmail account you want to connect, 
              or sign in with a new account.
            </Step>
            
            <Step number="4" title="Grant Permissions">
              <p>Google will ask you to grant the following permissions:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li><strong>Send email on your behalf</strong> - Required to send campaign emails</li>
                <li><strong>View your email address</strong> - Used to identify the account</li>
              </ul>
              <p className="mt-2">Click <strong>"Allow"</strong> to grant these permissions.</p>
            </Step>
            
            <Step number="5" title="Account Connected">
              You'll be redirected back to the Settings page. Your account will now appear 
              in the Email Accounts list with a green "Primary" badge.
            </Step>
          </div>

          <Tip type="info">
            <strong>Multiple Accounts:</strong> You can connect multiple Gmail accounts. 
            Click "Add Account" again to connect additional accounts. The first account 
            connected becomes the primary (default) account.
          </Tip>

          <div className="space-y-4 mt-6">
            <h3 className="font-semibold text-slate-900 text-lg">Managing Multiple Accounts</h3>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-amber-500 mt-0.5" />
                <div>
                  <strong className="text-slate-900">Setting a Primary Account</strong>
                  <p className="text-slate-600 text-sm">
                    Click "Set Primary" next to any account to make it the default for new campaigns.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Send className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <strong className="text-slate-900">Choosing Account Per Campaign</strong>
                  <p className="text-slate-600 text-sm">
                    When creating a campaign, use the "Send From" dropdown to select which account to send from.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Tip type="warning">
            <strong>Gmail Sending Limits:</strong> Gmail has daily sending limits. 
            Regular Gmail accounts can send up to 500 emails per day. 
            Google Workspace accounts can send up to 2,000 emails per day.
          </Tip>
        </div>
      </HowToSection>

      {/* Adding Contacts */}
      <HowToSection title="Adding Contacts" icon={Users}>
        <div className="space-y-6">
          <p className="text-slate-600">
            There are three ways to add contacts to your list: upload a spreadsheet, 
            search for businesses using Outscraper, or add them manually.
          </p>

          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload from Spreadsheet
            </h3>
            
            <Step number="1" title="Prepare Your File">
              Create a CSV or Excel file with your contacts. Include columns for email (required), 
              name, business name, phone, location, etc.
            </Step>
            
            <Step number="2" title="Upload the File">
              Go to <strong>Contacts</strong> and click <strong>"Upload File"</strong> in the 
              Upload Spreadsheet section.
            </Step>
            
            <Step number="3" title="Map Columns">
              Match your spreadsheet columns to the contact fields. At minimum, map the 
              email column. Other fields are optional.
            </Step>
            
            <Step number="4" title="Import">
              Click "Upload" to import your contacts. Duplicates are automatically skipped.
            </Step>
          </div>

          <div className="space-y-4 mt-6">
            <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
              <Search className="w-5 h-5" />
              Find Businesses with Outscraper
            </h3>
            
            <Step number="1" title="Enter Search Criteria">
              In the "Find Businesses" section, select a category (e.g., Plumber, Electrician) 
              and enter a location.
            </Step>
            
            <Step number="2" title="Adjust Settings">
              Set the search radius (in km) and the maximum number of results.
            </Step>
            
            <Step number="3" title="Start Search">
              Click "Start Search". The search runs in the background and may take a minute.
            </Step>
            
            <Step number="4" title="Review Results">
              Contacts with email addresses are automatically added to your list.
            </Step>
          </div>

          <div className="space-y-4 mt-6">
            <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Manually
            </h3>
            <p className="text-slate-600">
              Click the <strong>"Add Contact"</strong> button in the top right of the Contacts page 
              to add individual contacts by filling out the form.
            </p>
          </div>

          <Tip type="success">
            <strong>Pro Tip:</strong> Use tags to organize your contacts into segments 
            (e.g., "Hot Leads", "Plumbers", "London"). Click "Manage Tags" to create tags, 
            then assign them to contacts.
          </Tip>
        </div>
      </HowToSection>

      {/* Creating Templates */}
      <HowToSection title="Creating Email Templates" icon={FileText}>
        <div className="space-y-6">
          <p className="text-slate-600">
            Templates are reusable email designs that you can use across multiple campaigns.
          </p>

          <div className="space-y-4">
            <Step number="1" title="Go to Templates">
              Click <strong>Templates</strong> in the sidebar.
            </Step>
            
            <Step number="2" title="Create New Template">
              Click <strong>"New Template"</strong> and give it a name.
            </Step>
            
            <Step number="3" title="Set Subject Line">
              Enter the email subject line. You can use placeholders like {"{first_name}"} 
              that will be replaced with contact data.
            </Step>
            
            <Step number="4" title="Design Your Email">
              Use the rich text editor to create your email content. You can:
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Format text (bold, italic, headings)</li>
                <li>Add links to websites</li>
                <li>Insert images</li>
                <li>Create lists</li>
              </ul>
            </Step>
            
            <Step number="5" title="Save Template">
              Click <strong>"Save"</strong>. Your template is now ready to use in campaigns.
            </Step>
          </div>

          <Tip type="info">
            <strong>Personalization:</strong> Use these placeholders in your template:
            <code className="block bg-slate-100 p-2 rounded mt-2 text-xs">
              {"{first_name}"} {"{last_name}"} {"{business_name}"} {"{email}"}
            </code>
          </Tip>
        </div>
      </HowToSection>

      {/* Sending Campaigns */}
      <HowToSection title="Sending Email Campaigns" icon={Send}>
        <div className="space-y-6">
          <p className="text-slate-600">
            Campaigns let you send emails to groups of contacts using your templates.
          </p>

          <div className="space-y-4">
            <Step number="1" title="Create Campaign">
              Go to <strong>Campaigns</strong> and click <strong>"New Campaign"</strong>.
            </Step>
            
            <Step number="2" title="Configure Campaign">
              <ul className="list-disc ml-5 space-y-1">
                <li>Enter a campaign name</li>
                <li>Select an email template</li>
                <li>Choose which email account to send from (if you have multiple)</li>
              </ul>
            </Step>
            
            <Step number="3" title="Select Recipients">
              Add contacts using filters (category, location, rating) or search for individuals. 
              Only contacts with email addresses are shown.
            </Step>
            
            <Step number="4" title="A/B Testing (Optional)">
              Enable A/B testing to test different subject lines. Recipients are randomly 
              split between variants.
            </Step>
            
            <Step number="5" title="Schedule or Save">
              Choose to save as draft (send later) or schedule for a specific date/time.
            </Step>
            
            <Step number="6" title="Send Campaign">
              For draft campaigns, click the send button and confirm. Emails will be sent immediately.
            </Step>
          </div>

          <Tip type="success">
            <strong>Duplicate Campaigns:</strong> Click the copy icon on any campaign to 
            create a duplicate. This is useful for sending similar campaigns to different groups.
          </Tip>
        </div>
      </HowToSection>

      {/* Viewing Analytics */}
      <HowToSection title="Viewing Analytics" icon={BarChart3}>
        <div className="space-y-6">
          <p className="text-slate-600">
            Track the performance of your email campaigns with detailed analytics.
          </p>

          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 text-lg">Key Metrics</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <strong className="text-slate-900">Open Rate</strong>
                <p className="text-sm text-slate-600">
                  Percentage of recipients who opened your email. 
                  A good open rate is 20-30%.
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <strong className="text-slate-900">Click Rate</strong>
                <p className="text-sm text-slate-600">
                  Percentage of recipients who clicked a link in your email.
                  A good click rate is 2-5%.
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <strong className="text-slate-900">Bounce Rate</strong>
                <p className="text-sm text-slate-600">
                  Percentage of emails that couldn't be delivered.
                  Keep this under 2%.
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <strong className="text-slate-900">A/B Test Results</strong>
                <p className="text-sm text-slate-600">
                  Compare performance of subject line variants to find the winner.
                </p>
              </div>
            </div>
          </div>

          <Tip type="info">
            <strong>Dashboard:</strong> The main Dashboard shows a quick overview of your 
            key stats. For detailed campaign-by-campaign breakdown, visit the Analytics page.
          </Tip>
        </div>
      </HowToSection>

      {/* Troubleshooting */}
      <HowToSection title="Troubleshooting" icon={AlertCircle}>
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <strong className="text-slate-900">Emails not being received?</strong>
              <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc ml-5">
                <li>Check the recipient's spam/junk folder</li>
                <li>Verify the email address is correct</li>
                <li>Check if the contact has unsubscribed</li>
                <li>Gmail may rate-limit if sending too many emails quickly</li>
              </ul>
            </div>
            
            <div className="p-4 bg-slate-50 rounded-lg">
              <strong className="text-slate-900">Gmail won't connect?</strong>
              <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc ml-5">
                <li>Make sure you're signed into the correct Google account</li>
                <li>Try clearing your browser cookies and cache</li>
                <li>Disable browser extensions that might block popups</li>
                <li>Try using an incognito/private browser window</li>
              </ul>
            </div>
            
            <div className="p-4 bg-slate-50 rounded-lg">
              <strong className="text-slate-900">Outscraper search not finding emails?</strong>
              <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc ml-5">
                <li>Not all businesses have publicly listed email addresses</li>
                <li>Try different search terms or locations</li>
                <li>Increase the search limit for more results</li>
              </ul>
            </div>
          </div>
        </div>
      </HowToSection>

      {/* Need Help */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="pt-6">
          <div className="text-center">
            <h3 className="font-semibold text-slate-900 text-lg mb-2">Need More Help?</h3>
            <p className="text-slate-600 text-sm mb-4">
              If you're still having issues, please contact support with details about your problem.
            </p>
            <Button variant="outline" className="border-slate-300">
              <Mail className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpPage;
